/**
 * Your account: everything that is yours rather than the organisation's.
 *
 * The split is the one the top bar already draws — the gear is the
 * ORGANISATION, what the product does to every meeting in the tenant, decided
 * by an admin for everyone; the avatar is the PERSON.
 *
 * **Clerk owns the identity half.** Who you are, how you sign in, which
 * providers are connected, and second factors are all Clerk's data, and its
 * `<UserProfile>` is the maintained surface for them — reimplementing a
 * password change or an MFA enrolment against its API would be reimplementing
 * a security flow, which is the last thing worth owning a copy of. The old app
 * reached the same screens through `<UserButton>`; this mounts them as a page
 * instead, because they now sit beside two settings of our own.
 *
 * Those two are `<UserProfile.Page>` children rather than a separate route, so
 * "my account" is one place with one nav rather than two surfaces that each
 * know half the answer. Same arrangement the old app used.
 */
import { UserProfile } from '@clerk/clerk-react'
import { CalendarDays, Video } from 'lucide-react'
import { PageFrame } from '@/components/PageFrame'
import { currentPerson } from '@/data/people'
import { IS_DEV_AUTH } from '@/app/auth'
import { Calendars } from './Calendars'
import { RecordingPreferences } from './RecordingPreferences'

export function AccountPage() {
  const person = currentPerson()

  /**
   * The development bridge has no Clerk in the browser, so `<UserProfile>`
   * cannot mount. The two settings of our own still work — they are ours, not
   * Clerk's — so the page degrades to those rather than to an error.
   */
  if (IS_DEV_AUTH) {
    return (
      <PageFrame title="Your account" subtitle={person.email} width="narrow">
        <div className="space-y-4 py-1">
          <RecordingPreferences />
          <Calendars />
          <p className="px-1 text-2xs text-[var(--color-fg-subtle)]">
            Profile and security are Clerk's, and Clerk does not run on localhost under a
            production key. They appear here when deployed.
          </p>
        </div>
      </PageFrame>
    )
  }

  return (
    <PageFrame title="Your account" subtitle={person.email} width="wide">
      <div className="py-1">
        <UserProfile routing="hash">
          <UserProfile.Page
            label="Recording"
            labelIcon={<Video className="size-3.5" />}
            url="recording"
          >
            <RecordingPreferences />
          </UserProfile.Page>
          <UserProfile.Page
            label="Calendars"
            labelIcon={<CalendarDays className="size-3.5" />}
            url="calendars"
          >
            <Calendars />
          </UserProfile.Page>
        </UserProfile>
      </div>
    </PageFrame>
  )
}
